import React, { useRef, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { QRCode } from 'react-qrcode-logo';
import { useDispatch } from 'react-redux';

import { brandImage } from '../../../../config/branding';
import { setNotificationDetails } from '../../../../redux/slices/NotificationSlice';
import MyButton from '../../../../shared-components/button/Button';
import qrApi from '../../../../utils/api/qr';

/**
 * Device qr code view and download
 * @param {any} properties - The properties
 * @param {string} properties.deviceSN - The deviceSN prop
 * @param {() => void} properties.handleClose - The handleClose prop
 * @param {boolean} [properties.isRoaming] - The isRoaming prop
 * @param {string} [properties.cpoDomain] - The cpoDomain prop
 * @returns {any} The result
 */
function DeviceQrCode(properties: {
  deviceSN: string;
  handleClose: () => void;
  isRoaming?: boolean;
  cpoDomain?: string;
  proxyUrl?: string;
}): JSX.Element {
  const { t } = useTranslation('partnerDeviceManagement');
  const { deviceSN, handleClose, proxyUrl: providedProxyUrl } = properties;

  const dispatch = useDispatch();
  const qrCodeCanvasReference = useRef<HTMLDivElement | null>(null);

  const [proxyUrl, setProxyUrl] = useState<string>(providedProxyUrl || '');
  const [loading, setLoading] = useState<boolean>(!providedProxyUrl);
  const [error, setError] = useState<string>('');

  useEffect(() => {
    let isMounted = true;

    if (providedProxyUrl) {
      return () => {
        isMounted = false;
      };
    }

    /**
     * Fetches the QR proxy details from the backend.
     * @returns {Promise<void>} A promise that resolves when the fetch is complete.
     */
    const fetchQrDetails = async (): Promise<void> => {
      try {
        setLoading(true);
        const response = await qrApi.getProxyUrlByEvseId(deviceSN);
        if (isMounted) {
          setProxyUrl(response.data?.data?.proxyUrl || '');
        }
      } catch (error_: any) {
        if (isMounted) {
          setError(error_.response?.data?.message || t('somethingWentWrong'));
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchQrDetails();

    return () => {
      isMounted = false;
    };
  }, [deviceSN, t]);

  /**
   * Function to handle qr code download
   */
  const handleDownloadQRCode = () => {
    try {
      const canvas = qrCodeCanvasReference.current?.querySelector('canvas');
      if (canvas) {
        const link = document.createElement('a');
        link.href = canvas.toDataURL('image/png'); // Get the PNG data URL
        link.download = `${deviceSN}.png`; // File name for download
        link.click(); // Trigger the download
      }
      handleClose();
    } catch (error_) {
      dispatch(
        setNotificationDetails({
          type: 'error',
          notificationDetails: {
            statusCode: 200,
            message: t('somethingWentWrong'),
          },
        }),
      );
    }
  };

  /**
   * Renders the QR code content dynamically based on the current state.
   * @returns {JSX.Element} The rendered content.
   */
  const renderContent = (): JSX.Element => {
    if (loading) {
      return (
        <div className="device-qr-loading" style={{ padding: '40px', textAlign: 'center' }}>
          {t('loading')}...
        </div>
      );
    }
    if (error) {
      return (
        <div className="device-qr-error" style={{ padding: '40px', textAlign: 'center', color: 'red' }}>
          {error}
        </div>
      );
    }
    if (proxyUrl) {
      return (
        <div ref={qrCodeCanvasReference} className="device-qr-code">
          <QRCode
            size={300}
            ecLevel="H"
            qrStyle="squares"
            bgColor="#FFFFFF"
            fgColor="#000000"
            logoWidth={50}
            logoHeight={50}
            logoOpacity={1}
            quietZone={50}
            value={proxyUrl}
            logoImage={brandImage.qrLogo}
            removeQrCodeBehindLogo
          />
        </div>
      );
    }
    return (
      <div className="device-qr-error" style={{ padding: '40px', textAlign: 'center' }}>
        {t('noDataFound')}
      </div>
    );
  };

  return (
    <div className="device-qr-code-wrap-partner">
      <div className="device-qr-code-container">
        {renderContent()}
      </div>
      <div className="device-qr-code-btn">
        <MyButton label={t('cancel')} buttonType="back" onClickFunc={handleClose} />
        <MyButton 
          label={t('download')} 
          buttonType="submit" 
          onClickFunc={handleDownloadQRCode} 
          disableButton={loading || !!error || !proxyUrl} 
        />
      </div>
    </div>
  );
}

DeviceQrCode.defaultProps = {
  isRoaming: false,
  cpoDomain: '',
  proxyUrl: '',
};

export default DeviceQrCode;
